import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { toast } from "@/components/ui/sonner";
import { X, Plus, Trash2, GripVertical } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: "personal",  label: "Personal",  color: "#92400e",  bg: "rgba(212,165,116,0.15)" },
  { value: "financial", label: "Financial", color: "#3d6b1f",  bg: "rgba(61,107,31,0.12)"  },
  { value: "career",    label: "Career",    color: "#1A4D2E",  bg: "rgba(26,77,46,0.12)"   },
  { value: "health",    label: "Health",    color: "#dc2626",  bg: "rgba(220,38,38,0.1)"   },
  { value: "other",     label: "Other",     color: "#6b7280",  bg: "rgba(107,114,128,0.1)" },
];

const PRIORITIES = [
  { value: "low",    label: "Low",    color: "#3b82f6", bg: "rgba(59,130,246,0.1)"  },
  { value: "medium", label: "Medium", color: "#d97706", bg: "rgba(217,119,6,0.1)"   },
  { value: "high",   label: "High",   color: "#dc2626", bg: "rgba(220,38,38,0.1)"   },
];

const INPUT_STYLE = {
  width: "100%", boxSizing: "border-box",
  border: "1px solid rgba(26,77,46,0.2)",
  borderRadius: "0.5rem", padding: "0.6rem 0.8rem",
  fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", color: "#1A4D2E",
  background: "rgba(255,255,255,0.8)",
  outline: "none",
};

export default function CreateGoalModal({ goal, onClose, onSaved }) {
  const { token } = useAuth();
  const isEdit = !!goal;

  const [title, setTitle] = useState(goal?.title || "");
  const [category, setCategory] = useState(goal?.category || "personal");
  const [priority, setPriority] = useState(goal?.priority || "medium");
  const [targetDate, setTargetDate] = useState(goal?.target_date || "");
  const [targetAmount, setTargetAmount] = useState(goal?.target_amount || "");
  const [targetUnit, setTargetUnit] = useState(goal?.target_unit || "");
  const [motivationNotes, setMotivationNotes] = useState(goal?.motivation_notes || "");
  const [items, setItems] = useState(
    goal?.items?.map((i) => ({ ...i, _temp: false })) || []
  );
  const [newItemText, setNewItemText] = useState("");
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    setItems((prev) => [...prev, { id: `_new_${Date.now()}`, title: text, is_completed: false, _temp: true }]);
    setNewItemText("");
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Goal title is required"); return; }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        title: title.trim(), category, priority,
        target_date: targetDate || null,
        target_amount: targetAmount ? parseFloat(targetAmount) : null,
        target_unit: targetUnit || null,
        motivation_notes: motivationNotes || null,
        items: items.map((it, i) => ({ title: it.title, item_order: i })),
      };
      if (isEdit) {
        // Update goal metadata (items managed separately via add/delete)
        const { data } = await axios.patch(`${BACKEND_URL}/api/goals/${goal.id}`, payload, { headers });
        onSaved(data);
        toast.success("Goal updated");
      } else {
        const { data } = await axios.post(`${BACKEND_URL}/api/goals`, payload, { headers });
        onSaved(data);
        toast.success("Goal created");
      }
      onClose();
    } catch {
      toast.error("Failed to save goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="create-goal-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 80,
        background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(255,255,255,0.97)",
          borderRadius: "1.25rem", width: "100%", maxWidth: "540px",
          maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(26,77,46,0.22)",
          padding: "1.75rem",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontWeight: 800, fontSize: "1.2rem", margin: 0 }}>
            {isEdit ? "Edit Goal" : "Create New Goal"}
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "0.25rem" }}>
            <X size={18} />
          </button>
        </div>

        {/* Title */}
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.4rem" }}>
            Goal Title *
          </label>
          <input
            data-testid="goal-title-input"
            value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Sell home for $450k"
            style={INPUT_STYLE}
          />
        </div>

        {/* Category */}
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>
            Category
          </label>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                data-testid={`category-${c.value}`}
                onClick={() => setCategory(c.value)}
                style={{
                  padding: "0.35rem 0.8rem", borderRadius: "2rem", border: "1.5px solid",
                  borderColor: category === c.value ? c.color : "rgba(26,77,46,0.15)",
                  background: category === c.value ? c.bg : "transparent",
                  color: category === c.value ? c.color : "#6b7280",
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Priority */}
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>
            Priority
          </label>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                data-testid={`priority-${p.value}`}
                onClick={() => setPriority(p.value)}
                style={{
                  padding: "0.35rem 1rem", borderRadius: "2rem", border: "1.5px solid",
                  borderColor: priority === p.value ? p.color : "rgba(26,77,46,0.15)",
                  background: priority === p.value ? p.bg : "transparent",
                  color: priority === p.value ? p.color : "#6b7280",
                  fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", fontWeight: 600,
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Target Date + Amount row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem", marginBottom: "1.1rem" }}>
          <div>
            <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.4rem" }}>
              Target Date
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              style={INPUT_STYLE}
            />
          </div>
          <div>
            <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.4rem" }}>
              Target Metric
            </label>
            <div style={{ display: "flex", gap: "0.4rem" }}>
              <input
                value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="450000"
                type="number"
                style={{ ...INPUT_STYLE, width: "55%" }}
              />
              <input
                value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)}
                placeholder="$"
                style={{ ...INPUT_STYLE, width: "40%" }}
              />
            </div>
          </div>
        </div>

        {/* Motivation notes */}
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.4rem" }}>
            Why does this goal matter to you?
          </label>
          <textarea
            value={motivationNotes}
            onChange={(e) => setMotivationNotes(e.target.value)}
            placeholder="This will help me..."
            rows={2}
            style={{ ...INPUT_STYLE, resize: "vertical", lineHeight: 1.5 }}
          />
        </div>

        {/* Checklist items */}
        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ fontFamily: "'Manrope', sans-serif", color: "#374151", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "0.5rem" }}>
            Checklist Items
          </label>
          {items.map((item, idx) => (
            <div key={item.id || idx} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.4rem" }}>
              <GripVertical size={14} color="#9ca3af" style={{ flexShrink: 0 }} />
              <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem", color: "#374151", flex: 1 }}>
                {item.title}
              </span>
              <button onClick={() => removeItem(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "0.1rem" }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <input
              data-testid="new-checklist-item-input"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
              placeholder="Add a checklist item..."
              style={{ ...INPUT_STYLE, flex: 1 }}
            />
            <button
              onClick={addItem}
              style={{
                background: "rgba(26,77,46,0.08)", border: "1px solid rgba(26,77,46,0.2)",
                borderRadius: "0.5rem", padding: "0.5rem 0.7rem", cursor: "pointer", color: "#1A4D2E",
              }}
            >
              <Plus size={16} />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              padding: "0.65rem 1.4rem", borderRadius: "2rem",
              border: "1px solid rgba(26,77,46,0.2)", background: "transparent",
              fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", color: "#6b7280", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="save-goal-button"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "0.65rem 1.75rem", borderRadius: "2rem",
              background: saving ? "rgba(26,77,46,0.5)" : "#1A4D2E", border: "none", color: "#fff",
              fontFamily: "'Manrope', sans-serif", fontSize: "0.875rem", fontWeight: 700, cursor: "pointer",
              boxShadow: "0 2px 12px rgba(26,77,46,0.25)",
            }}
          >
            {saving ? "Saving..." : (isEdit ? "Save Changes" : "Create Goal")}
          </button>
        </div>
      </div>
    </div>
  );
}
